import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { toast } from "sonner";
import {
  Plus, Users, UserPlus, Scissors, MapPin, Package,
  Gift, Check, RefreshCw, Search, Trash2,
  ArrowLeft, Lock, ShieldCheck, Settings2, X
} from "lucide-react";

const SECTIONS = [
  { id: "staff", label: "Team Members", icon: UserPlus, color: "#8B5CF6", idKey: "user_id", nameKey: "name", endpoint: "/staff/all", deleteEndpoint: "/staff" },
  { id: "client", label: "Clients", icon: Users, color: "#2D5936", idKey: "client_id", nameKey: (c) => `${c.first_name} ${c.last_name}`, endpoint: "/clients", deleteEndpoint: "/clients" },
  { id: "service", label: "Services", icon: Scissors, color: "#F59E0B", idKey: "service_id", nameKey: "name", endpoint: "/services", deleteEndpoint: "/services" },
  { id: "location", label: "Locations", icon: MapPin, color: "#3B82F6", idKey: "location_id", nameKey: "name", endpoint: "/locations", deleteEndpoint: "/locations" },
  { id: "product", label: "Products", icon: Package, color: "#EC4899", idKey: "product_id", nameKey: "name", endpoint: "/products", deleteEndpoint: "/products" },
  { id: "giftcard", label: "Gift Cards", icon: Gift, color: "#10B981", idKey: "gift_card_id", nameKey: (g) => `$${g.amount} — ${g.code}`, endpoint: "/gift-cards", deleteEndpoint: "/gift-cards" },
];

/* ─── PIN Verify Modal ─── */
function PinModal({ open, onVerified, onClose }) {
  const [digits, setDigits] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [verifying, setVerifying] = useState(false);
  const refs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (open) { setDigits(["", "", "", ""]); setError(""); setTimeout(() => refs[0].current?.focus(), 120); }
  }, [open]);

  const handleChange = (idx, val) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...digits]; next[idx] = val; setDigits(next); setError("");
    if (val && idx < 3) refs[idx + 1].current?.focus();
    if (next.every(d => d !== "")) verify(next.join(""));
  };
  const handleKeyDown = (idx, e) => { if (e.key === "Backspace" && !digits[idx] && idx > 0) refs[idx - 1].current?.focus(); };
  const handlePaste = (e) => { const p = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4); if (p.length === 4) { setDigits(p.split("")); setError(""); refs[3].current?.focus(); verify(p); } };

  const verify = async (pin) => {
    setVerifying(true);
    try { await api.post("/settings/verify-pin", { pin }); onVerified(); }
    catch (e) { setError(e.response?.data?.detail || "Incorrect PIN"); setDigits(["", "", "", ""]); setTimeout(() => refs[0].current?.focus(), 120); }
    setVerifying(false);
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 w-[360px] p-8 space-y-6 animate-in slide-in-from-bottom-4 duration-300" onClick={e => e.stopPropagation()} data-testid="pin-modal">
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-slate-900 flex items-center justify-center shadow-lg">
            <Lock className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900 tracking-tight">Manager PIN</h2>
          <p className="text-sm text-slate-400 font-['Manrope']">Enter your 4-digit PIN to continue</p>
        </div>
        <div className="flex justify-center gap-3" onPaste={handlePaste}>
          {digits.map((d, i) => (
            <input key={i} ref={refs[i]} type="password" inputMode="numeric" maxLength={1} value={d}
              onChange={e => handleChange(i, e.target.value)} onKeyDown={e => handleKeyDown(i, e)} data-testid={`pin-digit-${i}`}
              className={`w-14 h-16 text-center text-2xl font-semibold rounded-2xl outline-none transition-all duration-200 font-['Manrope']
                ${error ? "border-2 border-red-400 bg-rose-50/50" : d ? "border-2 border-rose-500 bg-[#FAFAFA]" : "border-2 border-slate-200 bg-[#FAFAFA]"}
                focus:border-rose-500 focus:ring-2 focus:ring-rose-500/10`} />
          ))}
        </div>
        {error && <p className="text-sm text-red-500 text-center font-medium font-['Manrope']" data-testid="pin-error">{error}</p>}
        {verifying && <div className="flex justify-center"><RefreshCw className="w-4 h-4 animate-spin text-rose-400" /></div>}
        <button onClick={onClose} className="w-full text-center text-sm text-slate-400 hover:text-slate-900 transition-colors py-2 font-['Manrope']">Cancel</button>
      </div>
    </div>
  );
}

/* ─── PIN Setup Modal ─── */
function SetPinModal({ open, onSet, onClose }) {
  const [step, setStep] = useState(1);
  const [pin, setPin] = useState(["", "", "", ""]);
  const [confirm, setConfirm] = useState(["", "", "", ""]);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const pinRefs = [useRef(), useRef(), useRef(), useRef()];
  const confirmRefs = [useRef(), useRef(), useRef(), useRef()];

  useEffect(() => {
    if (open) { setStep(1); setPin(["", "", "", ""]); setConfirm(["", "", "", ""]); setError(""); setTimeout(() => pinRefs[0].current?.focus(), 120); }
  }, [open]);

  const handleChange = (arr, setArr, refs, idx, val, onComplete) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...arr]; next[idx] = val; setArr(next); setError("");
    if (val && idx < 3) refs[idx + 1].current?.focus();
    if (next.every(d => d !== "") && onComplete) onComplete(next);
  };
  const handleKeyDown = (arr, refs, idx, e) => { if (e.key === "Backspace" && !arr[idx] && idx > 0) refs[idx - 1].current?.focus(); };

  const goToConfirm = () => { setStep(2); setConfirm(["", "", "", ""]); setTimeout(() => confirmRefs[0].current?.focus(), 120); };

  const submitPin = async (confirmDigits) => {
    if (pin.join("") !== confirmDigits.join("")) {
      setError("PINs don't match. Try again."); setConfirm(["", "", "", ""]); setTimeout(() => confirmRefs[0].current?.focus(), 120); return;
    }
    setSaving(true);
    try { await api.post("/settings/manager-pin", { new_pin: pin.join("") }); toast.success("Manager PIN created"); onSet(); }
    catch (e) { setError(e.response?.data?.detail || "Failed to set PIN"); }
    setSaving(false);
  };

  if (!open) return null;
  const currentDigits = step === 1 ? pin : confirm;
  const currentRefs = step === 1 ? pinRefs : confirmRefs;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-md animate-in fade-in duration-200" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-100 w-[360px] p-8 space-y-6 animate-in slide-in-from-bottom-4 duration-300" onClick={e => e.stopPropagation()} data-testid="set-pin-modal">
        <div className="flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-full bg-emerald-600 flex items-center justify-center shadow-lg">
            <ShieldCheck className="w-6 h-6 text-white" strokeWidth={1.5} />
          </div>
          <h2 className="font-['Outfit'] text-xl font-semibold text-slate-900 tracking-tight">
            {step === 1 ? "Create Manager PIN" : "Confirm Your PIN"}
          </h2>
          <p className="text-sm text-slate-400 font-['Manrope']">
            {step === 1 ? "Choose a 4-digit PIN for protected actions" : "Re-enter your PIN to confirm"}
          </p>
        </div>
        <div className="flex justify-center gap-3">
          {currentDigits.map((d, i) => (
            <input key={`${step}-${i}`} ref={currentRefs[i]} type="password" inputMode="numeric" maxLength={1} value={d}
              onChange={e => {
                if (step === 1) handleChange(pin, setPin, pinRefs, i, e.target.value, (n) => { if (n.every(x => x !== "")) goToConfirm(); });
                else handleChange(confirm, setConfirm, confirmRefs, i, e.target.value, submitPin);
              }}
              onKeyDown={e => handleKeyDown(currentDigits, currentRefs, i, e)} data-testid={`set-pin-digit-${step}-${i}`}
              className={`w-14 h-16 text-center text-2xl font-semibold rounded-2xl outline-none transition-all duration-200 font-['Manrope']
                ${error ? "border-2 border-red-400 bg-rose-50/50" : d ? "border-2 border-emerald-500 bg-[#FAFAFA]" : "border-2 border-slate-200 bg-[#FAFAFA]"}
                focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/10`} />
          ))}
        </div>
        {error && <p className="text-sm text-red-500 text-center font-medium font-['Manrope']" data-testid="set-pin-error">{error}</p>}
        {saving && <div className="flex justify-center"><RefreshCw className="w-4 h-4 animate-spin text-rose-400" /></div>}
        <div className="flex items-center justify-between">
          <div className="flex gap-2">
            <div className={`w-2.5 h-2.5 rounded-full transition-colors ${step >= 1 ? "bg-emerald-600" : "bg-black/10"}`} />
            <div className={`w-2.5 h-2.5 rounded-full transition-colors ${step >= 2 ? "bg-emerald-600" : "bg-black/10"}`} />
          </div>
          <button onClick={onClose} className="text-sm text-slate-400 hover:text-slate-900 transition-colors font-['Manrope']">Cancel</button>
        </div>
      </div>
    </div>
  );
}

/* ─── Form Field Components ─── */
const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <label className="text-[11px] uppercase tracking-[0.15em] font-medium text-slate-400 font-['Manrope'] block">{label}</label>
    {children}
  </div>
);
const I = (props) => <Input {...props} className={`border-slate-200 h-10 text-sm font-['Manrope'] focus:border-rose-500 focus:ring-1 focus:ring-rose-500 bg-[#FAFAFA] rounded-lg ${props.className || ""}`} />;

/* ─── Add Form Content ─── */
function AddFormContent({ section, locations, staff, staffForm, setStaffForm, clientForm, setClientForm, serviceForm, setServiceForm, locationForm, setLocationForm, productForm, setProductForm, giftForm, setGiftForm }) {
  if (!section) return null;
  const id = section.id;

  if (id === "staff") return (
    <div className="grid grid-cols-2 gap-4">
      <F label="Name *"><I value={staffForm.name} onChange={e => setStaffForm(f => ({...f, name: e.target.value}))} data-testid="inp-staff-name" /></F>
      <F label="Email *"><I value={staffForm.email} onChange={e => setStaffForm(f => ({...f, email: e.target.value}))} /></F>
      <F label="Phone"><I value={staffForm.phone} onChange={e => setStaffForm(f => ({...f, phone: e.target.value}))} /></F>
      <F label="Title"><I value={staffForm.title} onChange={e => setStaffForm(f => ({...f, title: e.target.value}))} /></F>
      <F label="Role">
        <Select value={staffForm.role} onValueChange={v => setStaffForm(f => ({...f, role: v}))}>
          <SelectTrigger className="h-10 border-slate-200 text-sm bg-[#FAFAFA] rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="stylist">Stylist</SelectItem><SelectItem value="colorist">Colorist</SelectItem><SelectItem value="esthetician">Esthetician</SelectItem><SelectItem value="nail_tech">Nail Tech</SelectItem><SelectItem value="support_staff">Support Staff</SelectItem><SelectItem value="manager">Manager</SelectItem></SelectContent>
        </Select>
      </F>
      <F label="Location">
        <Select onValueChange={v => setStaffForm(f => ({...f, location_ids: [v]}))}>
          <SelectTrigger className="h-10 border-slate-200 text-sm bg-[#FAFAFA] rounded-lg"><SelectValue placeholder="Select" /></SelectTrigger>
          <SelectContent>{locations.map(l => <SelectItem key={l.location_id} value={l.location_id}>{l.name}</SelectItem>)}</SelectContent>
        </Select>
      </F>
      <F label="Employment">
        <Select value={staffForm.employment_type} onValueChange={v => setStaffForm(f => ({...f, employment_type: v}))}>
          <SelectTrigger className="h-10 border-slate-200 text-sm bg-[#FAFAFA] rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="full-time">Full-Time</SelectItem><SelectItem value="part-time">Part-Time</SelectItem><SelectItem value="contract">Contract</SelectItem></SelectContent>
        </Select>
      </F>
      <F label="Start Date"><I type="date" value={staffForm.start_date} onChange={e => setStaffForm(f => ({...f, start_date: e.target.value}))} /></F>
      <F label="Birthday"><I type="date" value={staffForm.birthday} onChange={e => setStaffForm(f => ({...f, birthday: e.target.value}))} /></F>
      <F label="Color"><I type="color" value={staffForm.color} onChange={e => setStaffForm(f => ({...f, color: e.target.value}))} className="p-1 h-10" /></F>
      <div className="col-span-2"><F label="Bio"><I value={staffForm.bio} onChange={e => setStaffForm(f => ({...f, bio: e.target.value}))} placeholder="Short bio..." /></F></div>
    </div>
  );

  if (id === "client") return (
    <div className="grid grid-cols-2 gap-4">
      <F label="First Name *"><I value={clientForm.first_name} onChange={e => setClientForm(f => ({...f, first_name: e.target.value}))} data-testid="inp-client-fname" /></F>
      <F label="Last Name *"><I value={clientForm.last_name} onChange={e => setClientForm(f => ({...f, last_name: e.target.value}))} /></F>
      <F label="Email"><I value={clientForm.email} onChange={e => setClientForm(f => ({...f, email: e.target.value}))} /></F>
      <F label="Phone"><I value={clientForm.phone} onChange={e => setClientForm(f => ({...f, phone: e.target.value}))} /></F>
      <F label="Birthday"><I type="date" value={clientForm.birthday} onChange={e => setClientForm(f => ({...f, birthday: e.target.value}))} /></F>
      <F label="Stage">
        <Select value={clientForm.pipeline_stage} onValueChange={v => setClientForm(f => ({...f, pipeline_stage: v}))}>
          <SelectTrigger className="h-10 border-slate-200 text-sm bg-[#FAFAFA] rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="lead">Lead</SelectItem><SelectItem value="consultation">Consultation</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="vip">VIP</SelectItem></SelectContent>
        </Select>
      </F>
      <div className="col-span-2"><F label="Notes"><I value={clientForm.notes} onChange={e => setClientForm(f => ({...f, notes: e.target.value}))} /></F></div>
    </div>
  );

  if (id === "service") return (
    <div className="grid grid-cols-2 gap-4">
      <F label="Name *"><I value={serviceForm.name} onChange={e => setServiceForm(f => ({...f, name: e.target.value}))} data-testid="inp-service-name" /></F>
      <F label="Category">
        <Select value={serviceForm.category} onValueChange={v => setServiceForm(f => ({...f, category: v}))}>
          <SelectTrigger className="h-10 border-slate-200 text-sm bg-[#FAFAFA] rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="Hair">Hair</SelectItem><SelectItem value="Color">Color</SelectItem><SelectItem value="Lashes & Brows">Lashes & Brows</SelectItem><SelectItem value="Nails">Nails</SelectItem><SelectItem value="Skin">Skin</SelectItem></SelectContent>
        </Select>
      </F>
      <F label="Duration (min)"><I type="number" min={15} step={15} value={serviceForm.duration} onChange={e => setServiceForm(f => ({...f, duration: +e.target.value}))} /></F>
      <F label="Price ($)"><I type="number" min={0} value={serviceForm.price} onChange={e => setServiceForm(f => ({...f, price: +e.target.value}))} /></F>
      <F label="Color"><I type="color" value={serviceForm.color} onChange={e => setServiceForm(f => ({...f, color: e.target.value}))} className="p-1 h-10" /></F>
      <F label="Description"><I value={serviceForm.description} onChange={e => setServiceForm(f => ({...f, description: e.target.value}))} /></F>
    </div>
  );

  if (id === "location") return (
    <div className="grid grid-cols-2 gap-4">
      <F label="Name *"><I value={locationForm.name} onChange={e => setLocationForm(f => ({...f, name: e.target.value}))} data-testid="inp-loc-name" /></F>
      <F label="Phone"><I value={locationForm.phone} onChange={e => setLocationForm(f => ({...f, phone: e.target.value}))} /></F>
      <div className="col-span-2"><F label="Address"><I value={locationForm.address} onChange={e => setLocationForm(f => ({...f, address: e.target.value}))} /></F></div>
      <F label="Email"><I value={locationForm.email} onChange={e => setLocationForm(f => ({...f, email: e.target.value}))} /></F>
      <F label="Hours"><I value={locationForm.hours} onChange={e => setLocationForm(f => ({...f, hours: e.target.value}))} /></F>
    </div>
  );

  if (id === "product") return (
    <div className="grid grid-cols-2 gap-4">
      <F label="Name *"><I value={productForm.name} onChange={e => setProductForm(f => ({...f, name: e.target.value}))} data-testid="inp-product-name" /></F>
      <F label="Brand"><I value={productForm.brand} onChange={e => setProductForm(f => ({...f, brand: e.target.value}))} /></F>
      <F label="Category">
        <Select value={productForm.category} onValueChange={v => setProductForm(f => ({...f, category: v}))}>
          <SelectTrigger className="h-10 border-slate-200 text-sm bg-[#FAFAFA] rounded-lg"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="hair_care">Hair Care</SelectItem><SelectItem value="color">Color</SelectItem><SelectItem value="styling">Styling</SelectItem><SelectItem value="skin_care">Skin Care</SelectItem><SelectItem value="tools">Tools</SelectItem></SelectContent>
        </Select>
      </F>
      <F label="SKU"><I value={productForm.sku} onChange={e => setProductForm(f => ({...f, sku: e.target.value}))} /></F>
      <F label="Price ($)"><I type="number" min={0} value={productForm.price} onChange={e => setProductForm(f => ({...f, price: +e.target.value}))} /></F>
      <F label="Cost ($)"><I type="number" min={0} value={productForm.cost} onChange={e => setProductForm(f => ({...f, cost: +e.target.value}))} /></F>
      <F label="Stock"><I type="number" min={0} value={productForm.stock} onChange={e => setProductForm(f => ({...f, stock: +e.target.value}))} /></F>
      <F label="Reorder Point"><I type="number" min={0} value={productForm.reorder_point} onChange={e => setProductForm(f => ({...f, reorder_point: +e.target.value}))} /></F>
    </div>
  );

  if (id === "giftcard") return (
    <div className="grid grid-cols-2 gap-4">
      <F label="Amount ($) *"><I type="number" min={10} value={giftForm.amount} onChange={e => setGiftForm(f => ({...f, amount: +e.target.value}))} data-testid="inp-gc-amount" /></F>
      <F label="Purchaser"><I value={giftForm.purchaser_name} onChange={e => setGiftForm(f => ({...f, purchaser_name: e.target.value}))} /></F>
      <F label="Recipient"><I value={giftForm.recipient_name} onChange={e => setGiftForm(f => ({...f, recipient_name: e.target.value}))} /></F>
      <F label="Recipient Email"><I value={giftForm.recipient_email} onChange={e => setGiftForm(f => ({...f, recipient_email: e.target.value}))} /></F>
    </div>
  );

  return null;
}

/* ─── Main Page ─── */
export default function CommandCenterPage() {
  const [mode, setMode] = useState("home");
  const [activeSection, setActiveSection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [search, setSearch] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [locations, setLocations] = useState([]);
  const [staff, setStaff] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetSection, setSheetSection] = useState(null);

  const [pinIsSet, setPinIsSet] = useState(null);
  const [showPinModal, setShowPinModal] = useState(false);
  const [showSetPinModal, setShowSetPinModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const [staffForm, setStaffForm] = useState({ name: "", email: "", phone: "", role: "stylist", title: "", location_ids: [], color: "#B5A493", birthday: "", bio: "", employment_type: "full-time", start_date: new Date().toISOString().slice(0, 10) });
  const [clientForm, setClientForm] = useState({ first_name: "", last_name: "", email: "", phone: "", birthday: "", preferred_location_id: "", tags: [], pipeline_stage: "active", notes: "" });
  const [serviceForm, setServiceForm] = useState({ name: "", category: "Hair", duration: 60, price: 0, description: "", location_ids: [], provider_ids: [], color: "#B5A493" });
  const [locationForm, setLocationForm] = useState({ name: "", address: "", phone: "", email: "", hours: "Mon-Sat 9am-7pm" });
  const [productForm, setProductForm] = useState({ name: "", brand: "", category: "hair_care", sku: "", price: 0, cost: 0, stock: 0, reorder_point: 5 });
  const [giftForm, setGiftForm] = useState({ amount: 50, recipient_name: "", recipient_email: "", purchaser_name: "", notes: "" });

  const [counts, setCounts] = useState({});

  const refreshData = async () => {
    const [l, s, sv, c] = await Promise.all([api.get("/locations"), api.get("/staff/all"), api.get("/services"), api.get("/clients?limit=100")]);
    setLocations(l.data || []);
    const staffData = (s.data || []).filter(u => ["stylist", "esthetician", "nail_tech", "owner", "colorist"].includes(u.role));
    setStaff(staffData);
    setServices(sv.data || []);
    const clientData = c.data?.clients || c.data || [];
    setClients(clientData);
    setCounts({ staff: (s.data || []).length, client: clientData.length, service: (sv.data || []).length, location: (l.data || []).length });
  };

  useEffect(() => {
    Promise.all([api.get("/locations"), api.get("/staff/all"), api.get("/services"), api.get("/clients?limit=100"), api.get("/settings/manager-pin/status")])
      .then(([l, s, sv, c, pinRes]) => {
        setLocations(l.data || []);
        const staffData = (s.data || []).filter(u => ["stylist", "esthetician", "nail_tech", "owner", "colorist"].includes(u.role));
        setStaff(staffData);
        setServices(sv.data || []);
        const clientData = c.data?.clients || c.data || [];
        setClients(clientData);
        setPinIsSet(pinRes.data?.is_set || false);
        setCounts({ staff: (s.data || []).length, client: clientData.length, service: (sv.data || []).length, location: (l.data || []).length });
      });
  }, []);

  const openManage = async (sec) => {
    setActiveSection(sec); setMode("manage"); setSearch(""); setDeleteConfirm(null); setLoadingItems(true);
    try { const res = await api.get(sec.endpoint); setItems(res.data?.clients || res.data || []); }
    catch { toast.error("Failed to load"); }
    setLoadingItems(false);
  };

  const openAdd = (sec) => {
    setSheetSection(sec); setSheetOpen(true);
    resetForm(sec.id);
  };

  const resetForm = (id) => {
    if (id === "staff") setStaffForm({ name: "", email: "", phone: "", role: "stylist", title: "", location_ids: [], color: "#B5A493", birthday: "", bio: "", employment_type: "full-time", start_date: new Date().toISOString().slice(0, 10) });
    if (id === "client") setClientForm({ first_name: "", last_name: "", email: "", phone: "", birthday: "", preferred_location_id: "", tags: [], pipeline_stage: "active", notes: "" });
    if (id === "service") setServiceForm({ name: "", category: "Hair", duration: 60, price: 0, description: "", location_ids: [], provider_ids: [], color: "#B5A493" });
    if (id === "location") setLocationForm({ name: "", address: "", phone: "", email: "", hours: "Mon-Sat 9am-7pm" });
    if (id === "product") setProductForm({ name: "", brand: "", category: "hair_care", sku: "", price: 0, cost: 0, stock: 0, reorder_point: 5 });
    if (id === "giftcard") setGiftForm({ amount: 50, recipient_name: "", recipient_email: "", purchaser_name: "", notes: "" });
  };

  const goHome = () => { setMode("home"); setActiveSection(null); setDeleteConfirm(null); };
  const getName = (sec, item) => typeof sec.nameKey === "function" ? sec.nameKey(item) : item[sec.nameKey] || "";

  // PIN gating
  const requirePin = (actionType, payload) => {
    if (pinIsSet === false) { setShowSetPinModal(true); setPendingAction({ type: actionType, payload }); return; }
    setPendingAction({ type: actionType, payload }); setShowPinModal(true);
  };
  const onPinVerified = () => {
    setShowPinModal(false);
    if (!pendingAction) return;
    if (pendingAction.type === "add") executeSubmit();
    else if (pendingAction.type === "delete") executeDelete(pendingAction.payload.sec, pendingAction.payload.itemId);
    setPendingAction(null);
  };
  const onPinSet = () => { setShowSetPinModal(false); setPinIsSet(true); setShowPinModal(true); };

  const deleteItem = (sec, itemId) => requirePin("delete", { sec, itemId });

  const executeDelete = async (sec, itemId) => {
    try {
      await api.delete(`${sec.deleteEndpoint}/${itemId}`);
      toast.success("Deleted successfully");
      setDeleteConfirm(null);
      setItems(prev => prev.filter(i => i[sec.idKey] !== itemId));
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const handleSubmitClick = () => requirePin("add", {});

  const executeSubmit = async () => {
    setSaving(true);
    try {
      const id = sheetSection.id;
      if (id === "staff") await api.post("/staff", staffForm);
      else if (id === "client") await api.post("/clients", clientForm);
      else if (id === "service") await api.post("/services", serviceForm);
      else if (id === "location") await api.post("/locations", locationForm);
      else if (id === "product") await api.post("/products", productForm);
      else if (id === "giftcard") await api.post("/gift-cards", giftForm);
      toast.success(`${sheetSection.label.slice(0, -1)} added!`);
      setSheetOpen(false);
      await refreshData();
      if (mode === "manage" && activeSection?.id === sheetSection.id) {
        const res = await api.get(activeSection.endpoint);
        setItems(res.data?.clients || res.data || []);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
    setSaving(false);
  };

  const filteredItems = items.filter(item => {
    if (!search) return true;
    return getName(activeSection, item).toLowerCase().includes(search.toLowerCase());
  });

  const getSubtext = (sec, item) => {
    if (sec.id === "staff") return `${item.role} ${item.email ? `· ${item.email}` : ""}`;
    if (sec.id === "client") return `${item.email || ""} ${item.phone ? `· ${item.phone}` : ""}`;
    if (sec.id === "service") return `${item.category || ""} · $${item.price} · ${item.duration}min`;
    if (sec.id === "location") return item.address || "";
    if (sec.id === "product") return `${item.brand || ""} · $${item.price} · Stock: ${item.stock ?? "?"}`;
    if (sec.id === "giftcard") return `${item.status} · Balance: $${item.balance}`;
    return "";
  };

  const getCount = (sec) => counts[sec.id] ?? "—";

  return (
    <div data-testid="command-center-page" className="min-h-screen bg-[#FAFAFA]">
      <PinModal open={showPinModal} onVerified={onPinVerified} onClose={() => { setShowPinModal(false); setPendingAction(null); }} />
      <SetPinModal open={showSetPinModal} onSet={onPinSet} onClose={() => { setShowSetPinModal(false); setPendingAction(null); }} />

      {/* Add Drawer */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent side="right" className="w-full sm:max-w-lg bg-white border-l border-slate-100 p-0 overflow-y-auto" data-testid={sheetSection ? `form-${sheetSection.id}` : "form-drawer"}>
          <div className="p-8 space-y-6">
            <SheetHeader className="space-y-1">
              <SheetTitle className="font-['Outfit'] text-2xl font-semibold text-slate-900 tracking-tight">
                Add {sheetSection?.label?.slice(0, -1)}
              </SheetTitle>
              <SheetDescription className="text-sm text-slate-400 font-['Manrope']">
                Fill in the details below to create a new {sheetSection?.label?.slice(0, -1).toLowerCase()}.
              </SheetDescription>
            </SheetHeader>
            <div className="border-t border-slate-100 pt-6">
              <AddFormContent section={sheetSection} locations={locations} staff={staff}
                staffForm={staffForm} setStaffForm={setStaffForm} clientForm={clientForm} setClientForm={setClientForm}
                serviceForm={serviceForm} setServiceForm={setServiceForm} locationForm={locationForm} setLocationForm={setLocationForm}
                productForm={productForm} setProductForm={setProductForm} giftForm={giftForm} setGiftForm={setGiftForm} />
            </div>
            <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
              <Button onClick={handleSubmitClick} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white rounded-lg px-6 h-11 font-['Manrope'] font-medium" data-testid="submit-form-btn">
                {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {saving ? "Adding..." : `Add ${sheetSection?.label?.slice(0, -1)}`}
              </Button>
              <Button variant="ghost" onClick={() => setSheetOpen(false)} className="text-slate-400 hover:text-slate-900 hover:bg-slate-50 rounded-lg h-11 font-['Manrope']">Cancel</Button>
              <span className="text-xs text-rose-400 flex items-center gap-1.5 ml-auto font-['Manrope']"><Lock className="w-3 h-3" strokeWidth={1.5} /> PIN required</span>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <div className="max-w-7xl mx-auto p-6 md:p-10 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            {mode === "manage" && (
              <button onClick={goHome} className="w-10 h-10 rounded-2xl border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 transition-colors" data-testid="back-btn">
                <ArrowLeft className="w-4 h-4 text-slate-900" strokeWidth={1.5} />
              </button>
            )}
            <div>
              <h1 className="font-['Outfit'] text-3xl font-semibold text-slate-900 tracking-tight">
                {mode === "home" ? "Command Center" : `Manage ${activeSection?.label}`}
              </h1>
              <p className="text-sm text-slate-400 mt-1 font-['Manrope']">
                {mode === "home" ? "Add or manage everything in one place" : `${filteredItems.length} ${activeSection?.label?.toLowerCase()}`}
              </p>
            </div>
          </div>
          <button
            onClick={() => pinIsSet ? setShowSetPinModal(true) : null}
            className="flex items-center gap-2.5 px-4 py-2 rounded-full border border-slate-200 bg-white text-sm font-medium font-['Manrope'] transition-all hover:bg-slate-50 hover:border-slate-300"
            data-testid="change-pin-btn"
          >
            <div className={`w-2 h-2 rounded-full ${pinIsSet ? "bg-green-500" : "bg-rose-400"}`} />
            <span className="text-slate-900">PIN {pinIsSet ? "Active" : "Not Set"}</span>
            {pinIsSet && <Settings2 className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.5} />}
          </button>
        </div>

        {/* Home Grid */}
        {mode === "home" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SECTIONS.map((sec, idx) => (
              <div
                key={sec.id}
                className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgba(0,0,0,0.02)] p-7 flex flex-col gap-6 justify-between transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_12px_32px_rgba(0,0,0,0.06)] hover:border-rose-300/30"
                style={{ animationDelay: `${idx * 60}ms`, animation: "fadeSlideUp 0.4s ease-out both" }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${sec.color}10` }}>
                      <sec.icon className="w-5 h-5" style={{ color: sec.color }} strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-['Outfit'] font-semibold text-slate-900 text-base">{sec.label}</h3>
                      <p className="text-xs text-slate-400 font-['Manrope'] mt-0.5">{getCount(sec)} total</p>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2.5">
                  <Button onClick={() => openAdd(sec)} className="flex-1 bg-slate-900 hover:bg-slate-800 text-white text-sm h-10 rounded-lg font-['Manrope'] font-medium transition-colors" data-testid={`add-${sec.id}-btn`}>
                    <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Add
                  </Button>
                  <Button variant="outline" onClick={() => openManage(sec)} className="flex-1 border-slate-200 text-slate-900 hover:bg-slate-50 text-sm h-10 rounded-lg font-['Manrope'] font-medium transition-colors" data-testid={`manage-${sec.id}-btn`}>
                    Manage
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Manage View */}
        {mode === "manage" && activeSection && (
          <div className="space-y-5">
            <div className="flex gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" strokeWidth={1.5} />
                <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${activeSection.label.toLowerCase()}...`}
                  className="pl-10 border-slate-200 h-11 bg-white rounded-2xl font-['Manrope'] text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500" data-testid="manage-search" />
              </div>
              <Button onClick={() => openAdd(activeSection)} className="bg-slate-900 hover:bg-slate-800 text-white h-11 rounded-2xl px-5 font-['Manrope'] font-medium" data-testid="manage-add-btn">
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Add New
              </Button>
            </div>

            {loadingItems ? (
              <div className="flex justify-center py-16"><RefreshCw className="w-5 h-5 animate-spin text-rose-400" /></div>
            ) : filteredItems.length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-2xl p-12 text-center">
                <p className="text-slate-400 font-['Manrope']">No {activeSection.label.toLowerCase()} found</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-2xl overflow-hidden shadow-[0_4px_24px_rgba(0,0,0,0.02)]">
                {filteredItems.map((item, idx) => {
                  const itemId = item[activeSection.idKey];
                  const name = getName(activeSection, item);
                  const sub = getSubtext(activeSection, item);
                  return (
                    <div key={itemId} data-testid={`item-${itemId}`}
                      className={`flex items-center gap-4 px-6 py-4 group transition-colors hover:bg-[#FAFAFA]/60 ${idx > 0 ? "border-t border-slate-100" : ""}`}>
                      <div className="w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${activeSection.color}10` }}>
                        <activeSection.icon className="w-4 h-4" style={{ color: activeSection.color }} strokeWidth={1.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate font-['Manrope']">{name}</p>
                        <p className="text-xs text-slate-400 truncate font-['Manrope'] mt-0.5">{sub}</p>
                      </div>
                      {deleteConfirm === itemId ? (
                        <div className="flex items-center gap-2 animate-in fade-in duration-200">
                          <span className="text-xs text-rose-600 font-medium font-['Manrope']">Confirm delete?</span>
                          <Button size="sm" onClick={() => deleteItem(activeSection, itemId)}
                            className="bg-rose-50 text-rose-600 hover:bg-rose-100 h-8 text-xs px-4 rounded-lg font-['Manrope'] font-medium" data-testid={`confirm-delete-${itemId}`}>
                            Delete
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => setDeleteConfirm(null)} className="h-8 text-xs px-3 rounded-lg text-slate-400 hover:bg-slate-50 font-['Manrope']">
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <button onClick={() => setDeleteConfirm(itemId)}
                          className="text-slate-300 hover:text-rose-500 transition-all opacity-0 group-hover:opacity-100 p-2 rounded-lg hover:bg-rose-50" data-testid={`delete-${itemId}`}>
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeSlideUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
